import { nodeConfig } from '@/mixins';
import { setHtmlStateLock, toggleHtmlClass } from '@/helpers/utils';

const NAMESPACE = 'modal';
const DEFAULT_SELECTOR = `[data-js-${NAMESPACE}]`;
const CLOSE_SELECTOR = `[data-js-${NAMESPACE}-close]`;

/**
 * @typedef {Object} ModalSettings
 * @property {boolean} [scrollLock=true] - Lock body scroll.
 * @property {boolean} [closeOnBackdropClick=false] - Close on backdrop click.
 * @property {boolean} [deeplink=false] - Enable URL hash updates.
 * @property {string|null} [htmlStateClass=null] - HTML class when open.
 */
const DEFAULT_SETTINGS = {
	scrollLock: true,
	closeOnBackdropClick: false,
	deeplink: false,
	htmlStateClass: null
};

/**
 * Native Dialog Wrapper.
 * Handles scroll locking and deep linking.
 *
 * @fires modal:open
 * @fires modal:close
 */
class Modal {
	/**
	 * @param {HTMLDialogElement} node
	 * @param {ModalSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;

		if (this.config.htmlStateClass === null && this.elNode.id) {
			this.config.htmlStateClass = `state-${this.elNode.id}-open`;
		}
	}

	init() {
		this._setup();
		this._bindUI();

		if (this.config.deeplink) {
			this._setupDeepLinks();
		}
	}

	/**
	 * Teardown listeners and reset state.
	 */
	destroy() {
		if (this.isOpen()) {
			this.elNode.close();
			this._onClose();
		}

		this._unbindUI();
	}

	/**
	 * @returns {boolean}
	 */
	isOpen() {
		return this.elNode.open;
	}

	/**
	 * Open the modal.
	 */
	open() {
		if (this.isOpen()) return;

		this.elNode.showModal();

		if (this.config.scrollLock) {
			setHtmlStateLock(true);
		}

		toggleHtmlClass(this.config.htmlStateClass, true);

		if (this.config.deeplink) updateHash(this.elNode.id, true);

		this._dispatch('open');
	}

	/**
	 * Close the modal.
	 */
	close() {
		if (!this.isOpen()) return;

		this.elNode.close();
	}

	_setup() {
		if (!this.elNode.hasAttribute('aria-modal')) {
			this.elNode.setAttribute('aria-modal', 'true');
		}

		if (!this.elNode.id) return;

		const triggers = document.querySelectorAll(`[data-js-modal-trigger="${this.elNode.id}"]`);

		triggers.forEach((trigger) => {
			if (!trigger.hasAttribute('aria-haspopup')) {
				trigger.setAttribute('aria-haspopup', 'dialog');
			}
		});
	}

	_setupDeepLinks() {
		if (!this.elNode.id) return;

		deeplink.register(`[data-js-modal]#${this.elNode.id}`, () => {
			if (this.isOpen()) return;

			this.open();
			this.elNode.focus();
		});
	}

	_dispatch(action) {
		this.elNode.dispatchEvent(
			new CustomEvent(`modal:${action}`, {
				bubbles: true,
				detail: { instance: this }
			})
		);
	}

	_bindUI() {
		this._onClose = this._onClose.bind(this);
		this._onBackdropClick = this._onBackdropClick.bind(this);
		this._onTriggerClick = this._onTriggerClick.bind(this);
		this._onCloserClick = this._onCloserClick.bind(this);

		this.elNode.addEventListener('close', this._onClose);
		this.elNode.addEventListener('click', this._onBackdropClick);
		this.elNode.addEventListener('click', this._onCloserClick);

		document.addEventListener('click', this._onTriggerClick);
	}

	_unbindUI() {
		this.elNode.removeEventListener('close', this._onClose);
		this.elNode.removeEventListener('click', this._onBackdropClick);
		this.elNode.removeEventListener('click', this._onCloserClick);
		document.removeEventListener('click', this._onTriggerClick);
	}

	_onTriggerClick(event) {
		const trigger = event.target.closest(`[data-js-modal-trigger="${this.elNode.id}"]`);

		if (!trigger) return;

		this.open();
	}

	_onCloserClick(event) {
		const closer = event.target.closest(CLOSE_SELECTOR);

		if (!closer) return;

		this.close();
	}

	_onClose() {
		if (this.config.scrollLock) {
			setHtmlStateLock(false);
		}

		toggleHtmlClass(this.config.htmlStateClass, false);

		if (this.config.deeplink) updateHash(this.elNode.id, false);

		this._dispatch('close');
	}

	/**
	 * Handle valid backdrop click.
	 * @protected
	 */
	_handleBackdropClick() {
		this.close();
	}

	_onBackdropClick(event) {
		if (!this.config.closeOnBackdropClick) return;
		if (event.target !== this.elNode) return;

		const rect = this.elNode.getBoundingClientRect();
		const isOutside =
			event.clientY < rect.top ||
			event.clientY > rect.bottom ||
			event.clientX < rect.left ||
			event.clientX > rect.right;

		if (!isOutside) return;

		this._handleBackdropClick();
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new Modal(node, config);

		module.init();

		return module;
	});
}